import React, { createContext, useCallback, useContext, useRef, useState } from 'react'
import { AlertTriangle, AlertOctagon, Info } from 'lucide-react'
import Modal from './Modal'

const VARIANTS = {
  default: {
    icon: Info,
    iconBg: 'bg-primary-50',
    iconColor: 'text-primary-500',
    confirmClass: 'btn-primary',
  },
  danger: {
    icon: AlertTriangle,
    iconBg: 'bg-red-50',
    iconColor: 'text-red-600',
    confirmClass:
      'inline-flex items-center justify-center px-4 py-2 rounded-lg font-semibold text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 transition-colors',
  },
  warning: {
    icon: AlertOctagon,
    iconBg: 'bg-amber-50',
    iconColor: 'text-amber-600',
    confirmClass:
      'inline-flex items-center justify-center px-4 py-2 rounded-lg font-semibold text-white bg-amber-600 hover:bg-amber-700 disabled:opacity-50 transition-colors',
  },
}

export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title = 'Are you sure?',
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'default',
  requireText,
  loading = false,
}) {
  const [typed, setTyped] = useState('')
  const confirmRef = useRef(null)
  const meta = VARIANTS[variant] || VARIANTS.default
  const Icon = meta.icon

  const matches = !requireText || typed === requireText

  const handleClose = () => {
    setTyped('')
    onClose?.()
  }

  const handleConfirm = () => {
    if (!matches || loading) return
    onConfirm?.()
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      size="sm"
      showCloseButton={false}
      initialFocusRef={requireText ? undefined : confirmRef}
      footer={
        <div className="flex gap-2 justify-end">
          <button
            type="button"
            onClick={handleClose}
            disabled={loading}
            className="btn-secondary"
          >
            {cancelLabel}
          </button>
          <button
            ref={confirmRef}
            type="button"
            onClick={handleConfirm}
            disabled={!matches || loading}
            className={meta.confirmClass}
          >
            {loading ? 'Working…' : confirmLabel}
          </button>
        </div>
      }
    >
      <div className="flex gap-4 items-start">
        <div className={`flex-shrink-0 w-10 h-10 rounded-full ${meta.iconBg} flex items-center justify-center`}>
          <Icon className={`h-5 w-5 ${meta.iconColor}`} aria-hidden="true" />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
          {message && (
            <p className="text-sm text-gray-600 mt-1 break-words">{message}</p>
          )}
          {requireText && (
            <div className="mt-3">
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Type <span className="font-mono bg-gray-100 px-1 py-0.5 rounded">{requireText}</span> to confirm
              </label>
              <input
                type="text"
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                className="input-field"
                autoFocus
                aria-label={`Type ${requireText} to confirm`}
              />
            </div>
          )}
        </div>
      </div>
    </Modal>
  )
}

const ConfirmContext = createContext(null)

export function ConfirmProvider({ children }) {
  const [state, setState] = useState({ open: false, options: {}, resolver: null, loading: false })

  const confirm = useCallback((options = {}) => {
    return new Promise((resolve) => {
      setState({ open: true, options, resolver: resolve, loading: false })
    })
  }, [])

  const handleClose = useCallback(() => {
    state.resolver?.(false)
    setState({ open: false, options: {}, resolver: null, loading: false })
  }, [state])

  const handleConfirm = useCallback(() => {
    state.resolver?.(true)
    setState({ open: false, options: {}, resolver: null, loading: false })
  }, [state])

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <ConfirmDialog
        open={state.open}
        onClose={handleClose}
        onConfirm={handleConfirm}
        loading={state.loading}
        {...state.options}
      />
    </ConfirmContext.Provider>
  )
}

export function useConfirm() {
  const ctx = useContext(ConfirmContext)
  if (!ctx) {
    throw new Error('useConfirm must be used within <ConfirmProvider>')
  }
  return ctx
}

export default ConfirmDialog
